
import { useState, useCallback } from 'react';
import { useAuth } from '@/components/AuthProvider';
import { toast } from '@/hooks/use-toast';
import { processJpegExport, downloadJpegFile } from './jpeg-export-process';

export const useJPEGExport = () => {
  const [isExporting, setIsExporting] = useState(false);
  const { user, isSessionExpired, refreshSession } = useAuth();

  const exportToJPEG = useCallback(async (
    drawingId: string, 
    title: string, 
    previewImage: string,
    analysisText?: string | null
  ) => {
    console.log('Enhanced JPEG export requested:', { drawingId, title });
    
    // Check authentication
    if (!user) {
      console.error('JPEG export failed: No authenticated user');
      toast({
        variant: "destructive",
        title: "Export failed",
        description: "Please log in to export drawings",
      });
      return;
    }

    // Handle expired session
    if (isSessionExpired) {
      console.log('Session expired, attempting refresh before export');
      try {
        const refreshed = await refreshSession();
        if (!refreshed) {
          toast({
            variant: "destructive",
            title: "Session expired",
            description: "Your session has expired. Please log in again.",
          });
          return;
        }
        console.log('Session refreshed successfully');
      } catch (error) {
        console.error('Session refresh error:', error);
        toast({
          variant: "destructive",
          title: "Authentication error",
          description: "Please try logging out and back in.",
        });
        return;
      }
    }

    setIsExporting(true);
    
    toast({
      title: "Preparing export",
      description: "Generating high-quality image...",
    });

    try {
      console.log('Processing enhanced JPEG export with content-aware rendering...');
      
      // Process the JPEG export with the content-focused template renderer
      const dataUrl = await processJpegExport(
        drawingId,
        title,
        previewImage,
        analysisText
      );
      
      // Download the file
      downloadJpegFile(dataUrl, drawingId, title);
      
      console.log('Enhanced JPEG export completed successfully');
    } catch (error) {
      console.error('JPEG export error:', error);
      toast({
        variant: "destructive",
        title: "Export failed",
        description: "There was an error exporting your drawing as JPEG. Please try again later.",
      });
    } finally {
      setIsExporting(false);
    }
  }, [user, isSessionExpired, refreshSession]);

  return {
    isExporting,
    exportToJPEG
  };
};
